import { create } from 'zustand';
import { FamilyMember, Family, Vehicle, Pet } from '../types';
import {
  getFamilyMembers,
  createFamilyMember,
  updateFamilyMember,
  deleteFamilyMember,
  getFamily,
  updateFamily,
  getVehicles,
  createVehicle,
  updateVehicle,
  deleteVehicle,
  getPets,
  createPet,
  updatePet,
  deletePet,
  getFamilyInvitations,
  inviteToFamily,
  cancelInvitation,
  resendInvitation,
  FamilyInvitation,
} from '../services';

interface FamilyState {
  // State
  family: Family | null;
  members: FamilyMember[];
  vehicles: Vehicle[];
  pets: Pet[];
  invitations: FamilyInvitation[];
  isLoading: boolean;
  error: string | null;
  lastFetchedAt: number | null;

  // Actions - Fetch
  fetchAll: () => Promise<void>;
  fetchFamily: () => Promise<void>;
  fetchMembers: () => Promise<void>;
  fetchVehicles: () => Promise<void>;
  fetchPets: () => Promise<void>;
  fetchInvitations: () => Promise<void>;

  // Actions - Family
  updateFamilyInfo: (data: Partial<Family>) => Promise<void>;

  // Actions - Members
  addMember: (data: Parameters<typeof createFamilyMember>[0]) => Promise<FamilyMember>;
  updateMember: (id: number, data: Partial<FamilyMember>) => Promise<FamilyMember>;
  removeMember: (id: number) => Promise<void>;

  // Actions - Vehicles
  addVehicle: (data: Parameters<typeof createVehicle>[0]) => Promise<Vehicle>;
  updateVehicleInfo: (id: number, data: Partial<Vehicle>) => Promise<Vehicle>;
  removeVehicle: (id: number) => Promise<void>;

  // Actions - Pets
  addPet: (data: Parameters<typeof createPet>[0]) => Promise<Pet>;
  updatePetInfo: (id: number, data: Partial<Pet>) => Promise<Pet>;
  removePet: (id: number) => Promise<void>;

  // Actions - Invitations
  invite: (email: string, role?: string) => Promise<void>;
  cancelInvite: (id: string) => Promise<void>;
  resendInvite: (id: string) => Promise<void>;

  // Utility
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  family: null,
  members: [],
  vehicles: [],
  pets: [],
  invitations: [],
  isLoading: false,
  error: null,
  lastFetchedAt: null,
};

const STALE_AFTER = 5 * 60 * 1000; // 5 minutes

export const useFamilyStore = create<FamilyState>()((set, get) => ({
  ...initialState,

  // Fetch all family data (SWR: skip spinner if data exists, skip fetch if fresh)
  fetchAll: async () => {
    const { lastFetchedAt, family } = get();
    const hasData = family !== null;
    const isFresh = lastFetchedAt !== null && Date.now() - lastFetchedAt < STALE_AFTER;

    if (hasData && isFresh) return;
    if (!hasData) set({ isLoading: true, error: null });

    try {
      const [familyRes, membersRes, vehiclesRes, petsRes, invitationsRes] = await Promise.all([
        getFamily(),
        getFamilyMembers(),
        getVehicles(),
        getPets(),
        getFamilyInvitations(),
      ]);
      set({
        family: familyRes,
        members: membersRes.members,
        vehicles: vehiclesRes.vehicles,
        pets: petsRes.pets,
        invitations: invitationsRes.invitations,
        isLoading: false,
        lastFetchedAt: Date.now(),
        error: null,
      });
    } catch (err) {
      if (!hasData) {
        set({
          error: err instanceof Error ? err.message : 'Failed to load family data',
          isLoading: false,
        });
      }
    }
  },

  fetchFamily: async () => {
    try {
      const family = await getFamily();
      set({ family });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to load family' });
    }
  },

  fetchMembers: async () => {
    try {
      const { members } = await getFamilyMembers();
      // A success retires this loader's earlier failure; left set, it was
      // re-announced by every later Settings sheet open (s6c r926 f2).
      set({ members, error: null });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to load members' });
    }
  },

  fetchVehicles: async () => {
    try {
      const { vehicles } = await getVehicles();
      set({ vehicles });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to load vehicles' });
    }
  },

  fetchPets: async () => {
    try {
      const { pets } = await getPets();
      set({ pets });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to load pets' });
    }
  },

  fetchInvitations: async () => {
    try {
      const { invitations } = await getFamilyInvitations();
      set({ invitations });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to load invitations' });
    }
  },

  // Family
  updateFamilyInfo: async (data) => {
    const family = await updateFamily(data);
    set({ family });
  },

  // Members
  addMember: async (data) => {
    const member = await createFamilyMember(data);
    set((state) => ({ members: [...state.members, member] }));
    return member;
  },

  updateMember: async (id, data) => {
    const member = await updateFamilyMember(id, data);
    set((state) => ({
      members: state.members.map((m) => (m.id === id ? member : m)),
    }));
    return member;
  },

  removeMember: async (id) => {
    await deleteFamilyMember(id);
    set((state) => ({
      members: state.members.filter((m) => m.id !== id),
    }));
  },

  // Vehicles
  addVehicle: async (data) => {
    const vehicle = await createVehicle(data);
    set((state) => ({ vehicles: [...state.vehicles, vehicle] }));
    return vehicle;
  },

  updateVehicleInfo: async (id, data) => {
    const vehicle = await updateVehicle(id, data);
    set((state) => ({
      vehicles: state.vehicles.map((v) => (v.id === id ? vehicle : v)),
    }));
    return vehicle;
  },

  removeVehicle: async (id) => {
    await deleteVehicle(id);
    set((state) => ({
      vehicles: state.vehicles.filter((v) => v.id !== id),
    }));
  },

  // Pets
  addPet: async (data) => {
    const pet = await createPet(data);
    set((state) => ({ pets: [...state.pets, pet] }));
    return pet;
  },

  updatePetInfo: async (id, data) => {
    const pet = await updatePet(id, data);
    set((state) => ({
      pets: state.pets.map((p) => (p.id === id ? pet : p)),
    }));
    return pet;
  },

  removePet: async (id) => {
    await deletePet(id);
    set((state) => ({
      pets: state.pets.filter((p) => p.id !== id),
    }));
  },

  // Invitations
  invite: async (email, role = 'member') => {
    await inviteToFamily(email, role);
    await get().fetchInvitations();
  },

  cancelInvite: async (id) => {
    await cancelInvitation(id);
    set((state) => ({
      invitations: state.invitations.filter((i) => i.id !== id),
    }));
  },

  resendInvite: async (id) => {
    await resendInvitation(id);
  },

  // Utility
  clearError: () => set({ error: null }),
  reset: () => set(initialState),
}));

// Selector hooks
export const useFamily = () => useFamilyStore((state) => state.family);
export const useFamilyMembers = () => useFamilyStore((state) => state.members);
export const useVehicles = () => useFamilyStore((state) => state.vehicles);
export const usePets = () => useFamilyStore((state) => state.pets);
export const useFamilyInvitations = () => useFamilyStore((state) => state.invitations);

export const isFamilyStale = () => {
  const { lastFetchedAt } = useFamilyStore.getState();
  return lastFetchedAt === null || Date.now() - lastFetchedAt > STALE_AFTER;
};

export default useFamilyStore;
